import React, { useCallback, useEffect } from "react";
import PropTypes from "prop-types";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useEngineConfigContext } from "../../Context/EngineConfigContext";
import { useAuthContext } from "../../Context/AuthContext";

export const ENGINE_OPTIONS = {
  Odoo: "Odoo"
};

export const VERSION_OPTIONS = {
  "14.0": "14.0",
  "15.0": "15.0",
  "16.0": "16.0"
};

const MAX_FIELD_LENGTH = 150;

const EngineConfigForm = ({ configId, variant, onClose }) => {
  const { fetchUserAttributes } = useAuthContext();
  const {
    fetchInfo,
    fetching,
    update,
    create,
    submitting,
    destroy,
    currentConfig,
    test,
    formValues,
    setFormValues,
    clearForm
  } = useEngineConfigContext();
  const { t } = useTranslation("common");
  const isEdit = Boolean(configId);
  const isModal = variant === "modal";

  useEffect(() => {
    if (configId) {
      fetchInfo(configId);
      return () => {
        setFormValues({});
      };
    }
    clearForm();
    return undefined;
  }, [configId]);

  useEffect(() => {
    if (currentConfig && configId) {
      setFormValues({
        name: currentConfig.name,
        type: currentConfig.type,
        version: currentConfig.version,
        database: currentConfig.database,
        url: currentConfig.url,
        username: currentConfig.username,
        password: currentConfig.password,
        description: currentConfig.description
      });
    }
  }, [currentConfig, configId]);

  const updateFormValue = (key, value) => {
    setFormValues({ ...formValues, [key]: value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (configId) {
      await update(configId, formValues);
    } else {
      await create(formValues);
    }
    await fetchUserAttributes();
    if (isModal || !configId) {
      onClose?.();
    }
  };

  const handleDelete = async () => {
    if (confirm(t("Are you sure? You want to delete this engine configuration!"))) {
      await destroy(configId);
      await fetchUserAttributes();
      onClose?.();
    }
  };

  const testConfig = useCallback(async () => {
    const payload = {
      engineName: formValues.type,
      dbName: formValues.database,
      url: formValues.url,
      userName: formValues.username,
      token: formValues.password
    };
    await test(payload);
  }, [formValues, test]);

  const handleClose = () => {
    clearForm();
    onClose?.();
  };

  if (fetching && isEdit) {
    return <div className="dashboard-loading">{t("Fetching...")}</div>;
  }

  const renderTextField = (id, labelKey, fieldKey, type = "text", inputProps = {}) => (
    <div className="engine-form-group">
      <label className="engine-form-label" htmlFor={id}>
        {t(labelKey)}
      </label>
      <input
        type={type}
        id={id}
        name={id}
        className="engine-form-input"
        value={formValues[fieldKey] || ""}
        disabled={fetching}
        maxLength={MAX_FIELD_LENGTH}
        onChange={(e) => updateFormValue(fieldKey, e.target.value)}
        {...inputProps}
      />
    </div>
  );

  const renderSelectField = (id, labelKey, fieldKey, options, placeholder) => (
    <div className="engine-form-group">
      <label className="engine-form-label" htmlFor={id}>
        {t(labelKey)}
      </label>
      <select
        id={id}
        name={id}
        className="engine-form-input"
        value={formValues[fieldKey] || ""}
        disabled={fetching}
        onChange={(e) => updateFormValue(fieldKey, e.target.value)}>
        <option value="">{placeholder}</option>
        {options.map(([value, label]) => (
          <option key={value} value={value}>
            {label}
          </option>
        ))}
      </select>
    </div>
  );

  return (
    <div className={`engine-form-classic${isModal ? " engine-form-classic-modal" : ""}`}>
      <div className="engine-form-classic-header">
        <h2>{isModal ? t("New Odoo Connection") : t("Engine Configuration")}</h2>
        {isModal ? (
          <button type="button" className="engine-form-close" onClick={handleClose} aria-label={t("Close")}>
            ✕
          </button>
        ) : (
          <Link to="/engine-config" className="engine-form-close" aria-label={t("Close")}>
            ✕
          </Link>
        )}
      </div>

      <form onSubmit={handleSubmit} id="engineForm">
        <div className="engine-form-classic-grid">
          <div className="engine-form-classic-col">
            {renderTextField("name", "Connection Name", "name")}
            {renderTextField("database", "Database Name", "database")}
            {renderTextField("username", "Username", "username")}
            {renderTextField("password", "Authorization Key", "password", "text", {
              autoComplete: "off"
            })}
          </div>

          <div className="engine-form-classic-col">
            {renderSelectField(
              "type",
              "Engine Type",
              "type",
              Object.entries(ENGINE_OPTIONS),
              t("Select an option")
            )}
            {renderSelectField(
              "version",
              "Engine Version",
              "version",
              Object.entries(VERSION_OPTIONS),
              t("Version")
            )}
            {renderTextField("url", "Engine URL", "url")}
            {renderTextField("description", "Description", "description")}
          </div>
        </div>

        <div className="engine-form-classic-actions">
          <button type="button" className="btn btn-ghost" disabled={submitting} onClick={testConfig}>
            {t("Test")}
          </button>
          <button type="submit" className="btn btn-gradient" disabled={submitting}>
            {t("Save")}
          </button>
          <button type="button" className="btn btn-ghost" disabled={submitting} onClick={clearForm}>
            {t("Clear")}
          </button>
          {!isModal && isEdit && (
            <button type="button" className="btn btn-danger" disabled={submitting} onClick={handleDelete}>
              {t("Delete")}
            </button>
          )}
        </div>
      </form>
    </div>
  );
};

EngineConfigForm.propTypes = {
  configId: PropTypes.string,
  variant: PropTypes.oneOf(["page", "modal"]),
  onClose: PropTypes.func
};

EngineConfigForm.defaultProps = {
  configId: null,
  variant: "page",
  onClose: null
};

export default EngineConfigForm;
